import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import {
  LayoutDashboard, ClipboardList, Package, Calendar, ShieldCheck,
  Users, BarChart3, Settings, Bell, ChevronDown, LogOut,
  Building2, Menu, X, AlertTriangle, Sparkles, ClipboardCheck, ArrowRightLeft,
  QrCode, FolderOpen, Archive, FileStack, Globe,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { cn } from '@/lib/utils'
import { AIChatPanel } from '@/components/AIChatPanel'
import { MobileBottomNav } from '@/components/layout/MobileBottomNav'

const NAV_ITEMS = [
  { to: '/dashboard',   label: 'Dashboard',     icon: LayoutDashboard },
  { to: '/aida',        label: 'Aida Chat',     icon: Sparkles },
  { to: '/work-orders', label: 'Work Orders',    icon: ClipboardList },
  { to: '/assets',      label: 'Assets',         icon: Package },
  { to: '/qr-scanner',  label: 'QR Scanner',     icon: QrCode },
  { to: '/ppm',         label: 'PPM Schedules',  icon: Calendar },
  { to: '/compliance',  label: 'Compliance',     icon: ShieldCheck },
  { to: '/safety',      label: 'Safety Log',     icon: ClipboardCheck },
  { to: '/handover',    label: 'Shift Handover', icon: ArrowRightLeft },
  { to: '/inventory',   label: 'Inventory',      icon: Package },
  { to: '/vendors',     label: 'Contractors',    icon: Users },
  { to: '/cases',       label: 'Cases',          icon: FolderOpen },
  { to: '/retention',   label: 'Retention',      icon: Archive },
  { to: '/reports',     label: 'Report Packs',   icon: FileStack },
  { to: '/portal',      label: 'External Portal', icon: Globe },
  { to: '/users',       label: 'Team',           icon: Users },
  { to: '/analytics',   label: 'Analytics',      icon: BarChart3 },
  { to: '/settings',    label: 'Settings',       icon: Settings },
]

export function AppLayout() {
  const { user, logout, currentPropertyId, setCurrentProperty } = useAuthStore()
  const navigate = useNavigate()
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [chatOpen, setChatOpen] = useState(false)

  const currentProperty = user?.propertyAccess.find(
    (p) => p.propertyId === currentPropertyId
  )?.property

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Mobile sidebar overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Sidebar — hidden on mobile, shown on desktop */}
      <aside
        className={cn(
          'flex flex-col bg-navy-800 transition-all duration-200 flex-shrink-0',
          // Desktop
          'hidden md:flex',
          sidebarOpen ? 'w-60' : 'w-16',
        )}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-navy-700">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/logo-icon.png" alt="EstateKey" className="w-8 h-8 object-contain flex-shrink-0" />
            {sidebarOpen && (
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm truncate">EstateKey</p>
                <p className="text-navy-300 text-xs truncate">{user?.group?.name}</p>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="ml-auto text-navy-300 hover:text-white p-1 rounded"
          >
            {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>

        {/* Property switcher */}
        {sidebarOpen && (
          <div className="px-3 py-2 border-b border-navy-700">
            <select
              value={currentPropertyId ?? ''}
              onChange={(e) => setCurrentProperty(e.target.value)}
              className="w-full bg-navy-700 text-white text-xs rounded-lg px-2 py-1.5
                         border border-navy-600 focus:outline-none focus:border-gold-500"
            >
              {user?.propertyAccess.map((access) => (
                <option key={access.propertyId} value={access.propertyId}>
                  {access.property?.name ?? access.propertyId}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
                  isActive
                    ? 'bg-gold-500 text-white font-medium'
                    : 'text-navy-200 hover:bg-navy-700 hover:text-white'
                )
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* User */}
        <div className="p-3 border-t border-navy-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-navy-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-semibold">
                {user?.firstName[0]}{user?.lastName[0]}
              </span>
            </div>
            {sidebarOpen && (
              <>
                <div className="min-w-0 flex-1">
                  <p className="text-white text-xs font-medium truncate">
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className="text-navy-300 text-xs truncate">{user?.role}</p>
                </div>
                <button onClick={handleLogout} className="text-navy-300 hover:text-white">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile sidebar drawer */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 bg-navy-800 flex flex-col transition-transform duration-300 md:hidden',
          mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex items-center h-16 px-4 border-b border-navy-700">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-gold-500 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-semibold text-sm truncate">EstateKey</p>
              <p className="text-navy-300 text-xs truncate">{user?.group?.name}</p>
            </div>
          </div>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="ml-auto text-navy-300 hover:text-white p-2 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Mobile property switcher */}
        <div className="px-3 py-2 border-b border-navy-700">
          <select
            value={currentPropertyId ?? ''}
            onChange={(e) => setCurrentProperty(e.target.value)}
            className="w-full bg-navy-700 text-white text-xs rounded-lg px-2 py-1.5
                       border border-navy-600 focus:outline-none focus:border-gold-500"
          >
            {user?.propertyAccess.map((access) => (
              <option key={access.propertyId} value={access.propertyId}>
                {access.property?.name ?? access.propertyId}
              </option>
            ))}
          </select>
        </div>

        {/* Mobile nav */}
        <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-0.5">
          {NAV_ITEMS.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setMobileSidebarOpen(false)}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors',
                  isActive
                    ? 'bg-gold-500 text-white font-medium'
                    : 'text-navy-200 hover:bg-navy-700 hover:text-white'
                )
              }
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Mobile user */}
        <div className="p-3 border-t border-navy-700">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-navy-600 flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-semibold">
                {user?.firstName[0]}{user?.lastName[0]}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-xs font-medium truncate">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-navy-300 text-xs truncate">{user?.role}</p>
            </div>
            <button onClick={handleLogout} className="text-navy-300 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top bar */}
        <header className="h-14 md:h-16 bg-white border-b border-gray-100 flex items-center px-4 md:px-6 gap-3 flex-shrink-0">
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileSidebarOpen(true)}
            className="md:hidden text-gray-500 hover:text-navy-800 p-1"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-navy-800 font-semibold text-sm truncate">
              {currentProperty?.name ?? 'Select a property'}
            </h1>
          </div>
          <button
            onClick={() => setChatOpen(!chatOpen)}
            className={cn(
              'relative p-2 rounded-lg transition-colors',
              chatOpen ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:text-navy-800'
            )}
            title="Aida"
          >
            <img src="/aida-icon.png" alt="Aida" className="w-6 h-6 object-contain" />
          </button>
          <button className="relative text-gray-400 hover:text-navy-800 transition-colors">
            <Bell className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
              3
            </span>
          </button>
        </header>

        {/* Page content — extra bottom padding on mobile for bottom nav */}
        <main className="flex-1 overflow-y-auto p-4 md:p-6 pb-20 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* AI Chat Panel */}
      <AIChatPanel open={chatOpen} onClose={() => setChatOpen(false)} />

      {/* Mobile bottom navigation */}
      <MobileBottomNav onMenuOpen={() => setMobileSidebarOpen(true)} />
    </div>
  )
}
